import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useInstagramConnection } from 'contexts/InstagramConnectionContext';
import VerificationCodeModal from 'components/modals/VerificationCodeModal';
import DeviceConfirmationModal from 'components/modals/DeviceConfirmationModal';
import { useTheme } from 'contexts/ThemeContext';

const shimmerKeyframes = `
@keyframes connectingShimmer {
  0% { left: -40%; }
  100% { left: 100%; }
}
`;

const ConnectingBanner: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { connectionState, resetConnection } = useInstagramConnection();
  const { isDark } = useTheme();
  const [showCodeModal, setShowCodeModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  // Auto-open verification code modal when code step is reached
  useEffect(() => {
    if (connectionState.step === 'code') {
      setShowCodeModal(true);
      setShowConfirmationModal(false);
    }
  }, [connectionState.step]);

  // Auto-open device confirmation modal when confirmation step is reached
  useEffect(() => {
    if (connectionState.step === 'confirmation') {
      setShowConfirmationModal(true);
      setShowCodeModal(false);
    }
  }, [connectionState.step]);

  // Show banner when actively connecting, code requested, confirmation requested, method requested, or error
  if (
    connectionState.step !== 'connecting' &&
    connectionState.step !== 'code' &&
    connectionState.step !== 'confirmation' &&
    connectionState.step !== 'method' &&
    connectionState.step !== 'error'
  ) {
    return null;
  }

  const isError = connectionState.step === 'error';
  const isVerificationRequired = connectionState.step === 'code';
  const isConfirmation = connectionState.step === 'confirmation';
  const isActivelyConnecting = !isError && !isVerificationRequired && !isConfirmation;

  const bannerBg = isDark ? '#0F172B' : '#FFFFFF';
  const bannerShadow = isDark
    ? '0px 10px 15px -3px rgba(2, 6, 24, 0.5), 0px 4px 6px -2px rgba(0, 0, 0, 0.05)'
    : '0px 10px 15px -3px rgba(43, 127, 255, 0.1), 0px 4px 6px -2px rgba(0, 0, 0, 0.05)';
  const titleColor = isDark ? '#E2E8F0' : '#314158';
  const subtitleColor = isDark ? '#90A1B9' : '#62748E';

  // Gradient border: red for error, blue→purple for normal
  const gradientBorder = isError
    ? `linear-gradient(90deg, #FF2056 0%, #FF6B8A 10%, ${bannerBg} 25%)`
    : `linear-gradient(90deg, #2B7FFF 0%, #AD46FF 10%, ${bannerBg} 25%)`;

  const handleTryReconnect = () => {
    const username = connectionState.username;
    resetConnection();
    // Navigate to accounts with re-auth for the same username
    navigate('/accounts', { state: { autoOpenOverlay: true, reauthUsername: username } });
  };

  // Determine banner title
  const getBannerTitle = () => {
    if (isError) return t('addAccount.connectionIssue') || 'Connection issue';
    if (isVerificationRequired) return t('addAccount.verificationRequired') || 'Verification required';
    if (isConfirmation) return t('addAccount.confirmDeviceLogin') || 'Please confirm device login';
    return t('addAccount.connectingBanner') || 'Connecting Instagram...';
  };

  // Determine banner subtitle
  const getBannerSubtitle = () => {
    if (isError) return connectionState.errorMessage || t('addAccount.connectionIssueDescription') || "We couldn't connect to your Instagram account right now.";
    if (isVerificationRequired) return connectionState.status || t('addAccount.verificationBannerSub') || 'Instagram sent a verification code to your email address.';
    if (isConfirmation) return connectionState.status || t('addAccount.confirmDeviceLoginDescription') || 'Please confirm the Instagram login on your device.';
    return connectionState.status || t('addAccount.connectingBannerSub') || 'This usually takes around 5 minutes.';
  };

  return (
    <>
      <div className="z-30" style={{ padding: '8px 8px 0px 0px', background: isDark ? '#1D293D' : '#F8FAFC' }}>
        {/* Outer wrapper for gradient border with rounded corners */}
        <div
          style={{
            padding: '2px',
            borderRadius: '12px',
            background: gradientBorder,
          }}
        >
          <div
            className="w-full flex flex-row items-center justify-between gap-2"
            style={{
              padding: '12px 16px',
              background: bannerBg,
              boxShadow: bannerShadow,
              borderRadius: '10px',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <div className="flex flex-col justify-center items-start">
              <p
                className="text-sm font-medium leading-5"
                style={{ color: titleColor }}
              >
                {getBannerTitle()}
              </p>
              <p
                className="text-sm font-normal leading-5"
                style={{ color: subtitleColor }}
              >
                {getBannerSubtitle()}
              </p>
            </div>

            {/* Error: Try to reconnect button */}
            {isError && (
              <button
                onClick={handleTryReconnect}
                className="flex-shrink-0 text-sm font-medium text-white px-3 py-2 transition-opacity hover:opacity-90"
                style={{
                  background: '#FF2056',
                  borderRadius: '8px',
                  height: '36px',
                  whiteSpace: 'nowrap',
                }}
              >
                {t('addAccount.tryToReconnect') || 'Try to reconnect'}
              </button>
            )}

            {/* Enter verification code button — only shown when code is requested */}
            {isVerificationRequired && (
              <button
                onClick={() => setShowCodeModal(true)}
                className="flex-shrink-0 text-sm font-medium text-white px-4 py-2 transition-opacity hover:opacity-90"
                style={{
                  background: 'linear-gradient(90deg, #2B7FFF 0%, #AD46FF 100%)',
                  borderRadius: '8px',
                  height: '36px',
                  whiteSpace: 'nowrap',
                }}
              >
                {t('addAccount.enterVerificationCode') || 'Enter verification code'}
              </button>
            )}

            {/* Animated progress shimmer at bottom — only during active connection */}
            {isActivelyConnecting && (
              <>
                <style>{shimmerKeyframes}</style>
                <div
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: '3px',
                    background: isDark
                      ? 'rgba(43, 127, 255, 0.15)'
                      : 'rgba(43, 127, 255, 0.1)',
                    overflow: 'hidden',
                    borderRadius: '0 0 10px 10px',
                  }}
                >
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      width: '40%',
                      height: '100%',
                      background: 'linear-gradient(90deg, transparent, #2B7FFF, #AD46FF, transparent)',
                      animation: 'connectingShimmer 1.8s ease-in-out infinite',
                    }}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Verification code modal */}
      <VerificationCodeModal
        isOpen={showCodeModal}
        onClose={() => setShowCodeModal(false)}
      />

      {/* Device confirmation modal */}
      <DeviceConfirmationModal
        isOpen={showConfirmationModal}
        onClose={() => setShowConfirmationModal(false)}
      />
    </>
  );
};

export default ConnectingBanner;
